import { isStaffRole, type Ruolo, type UUID } from '@/types';

/**
 * Every route in one place. Detail routes are functions so no component ever
 * builds a URL by string concatenation — a rename here is a compile error at
 * each call site rather than a dead link found in production.
 */
export const paths = {
  /** Public marketing home. Authenticated visits still render the app dashboard. */
  home: '/',
  login: '/login',
  registrazione: '/registrazione',
  verifyEmail: '/verify-email',
  forgotPassword: '/password/richiesta',
  resetPassword: '/password/reset',

  dashboard: '/',
  /** Appointments. Staff see every company; tenant users see their own company. */
  calendario: '/calendario',
  sicurezza: '/sicurezza',

  servizi: {
    root: '/servizi',
    /** @deprecated Use the workbench tab `${paths.servizi.root}?tab=macchine`. */
    macchine: '/servizi/macchine',
    macchina: (id: UUID) => `/servizi/macchine/${id}`,
    backup: '/servizi/backup',
    backupJob: (id: UUID) => `/servizi/backup/${id}`,
    /** One route serves firewall, antivirus and syslog – they share a shape. */
    modulo: (modulo: string) => `/servizi/${modulo}`,
  },

  ticket: {
    root: '/ticket',
    nuovo: '/ticket/nuovo',
    inLavorazione: '/ticket/in-lavorazione',
    chiusi: '/ticket/chiusi',
    detail: (id: UUID) => `/ticket/${id}`,
  },

  /** One page with tabs: the three report families share the period selector. */
  report: {
    root: '/report',
  },

  admin: {
    dashboard: '/admin',
    /** Work queues: attività, open tickets, tickets still to assign. */
    operativa: '/admin/operativa',
    clienti: '/admin/clienti',
    lead: '/admin/lead',
    /** Same page as `paths.calendario`. Kept so existing staff links stay valid. */
    leadCalendario: '/calendario',
    leadImport: '/admin/lead/import',
    leadDetail: (id: UUID) => `/admin/lead/${id}`,
    clientiPersi: '/admin/clienti-persi',
    cliente: (id: UUID) => `/admin/clienti/${id}`,
    contratti: '/admin/contratti',
    contratto: (id: UUID) => `/admin/contratti/${id}`,
    /** @deprecated Use `admin.catalog.products`. */
    prodotti: '/admin/prodotti',
    /** @deprecated Use `admin.catalog.services`. */
    serviziCatalogo: '/admin/servizi',
    catalog: {
      products: '/admin/catalogo/prodotti',
      services: '/admin/catalogo/servizi',
    },
    macchine: '/admin/macchine',
    /** Placeholder under Macchine — health overview TBD. */
    statoSalute: '/admin/macchine/stato-di-salute',
    /**
     * Backup monitoring → Alert (former Macchine in allarme).
     * @deprecated Prefer `admin.backup.alert`.
     */
    machineAlerts: '/admin/backup/alert',
    codaTicket: '/admin/ticket',
    /** @deprecated Use `admin.backup.pending`. */
    backupPending: '/admin/backup-pending',
    backup: {
      /** Staff alias of the tenant backup job list. Same page as `servizi.backup`. */
      jobs: '/admin/backup',
      /** Machines in error / warning / silent — same surface as the old Macchine in allarme. */
      alert: '/admin/backup/alert',
      pending: '/admin/backup/pending',
    },
    /** @deprecated Use `admin.activities.list`. */
    tasks: '/admin/tasks',
    /** @deprecated Use `admin.activities.detail`. */
    task: (id: UUID) => `/admin/tasks/${id}`,
    activities: {
      list: '/admin/attivita',
      detail: (id: UUID) => `/admin/attivita/${id}`,
    },
    categorie: '/admin/categorie',
    report: '/admin/report',
    cedolini: '/admin/cedolini',
    cedolino: (id: UUID) => `/admin/cedolini/${id}`,
    /** Demo shell. Invoicing is not wired. */
    fatture: '/admin/fatture',
    preventivi: '/admin/preventivi',
    preventivoNuovo: '/admin/preventivi/nuovo',
    preventivoPerLead: (id: UUID) => `/admin/preventivi/nuovo?lead=${id}`,
    preventiviMensili: '/admin/preventivi/mensili',
    preventiviImpostazioni: '/admin/preventivi/impostazioni',
    preventivo: (id: UUID) => `/admin/preventivi/${id}`,
    documenti: '/admin/documenti',
    documento: (id: UUID) => `/admin/documenti/${id}`,
    documentiVisibilita: '/admin/documenti/visibilita',
    /** Staff-only client folders. Not visible in the tenant portal. */
    documentazioneInterna: '/admin/documenti/interna',
    documentazioneCliente: (id: UUID) => `/admin/documenti/interna/${id}`,
    frameworkMatrix: '/admin/framework-matrix',
    sicurezza: '/admin/sicurezza',
    users: '/admin/utenti',
  },

  cedolini: {
    root: '/cedolini',
    detail: (id: UUID) => `/cedolini/${id}`,
  },

  preventivi: {
    root: '/preventivi',
    detail: (id: UUID) => `/preventivi/${id}`,
  },

  contratti: {
    root: '/contratti',
    detail: (id: UUID) => `/contratti/${id}`,
    /** Dedicated signing flow for the customer portal. */
    firma: (id: UUID) => `/contratti/${id}/firma`,
  },

  organizzazione: '/organizzazione',

  documenti: {
    root: '/documenti',
    detail: (id: UUID) => `/documenti/${id}`,
    visibilita: '/documenti/visibilita',
  },

  utenti: '/utenti',
  impostazioni: '/impostazioni',
  /** Platform-admin Vaultwarden instance manager (linked from Profilo). */
  vaultwarden: '/impostazioni/vaultwarden',
  /** Deep-link: focus Vaultwarden settings for one organizzazione. */
  vaultwardenCliente: (clienteId: UUID) => `/impostazioni/vaultwarden?clienteId=${clienteId}`,
  notifiche: '/notifiche',
  notFound: '/404',
  forbidden: '/403',
} as const;

export function ticketCreateHref(prefill?: {
  clienteId?: string | null;
  macchinaId?: string | null;
  sedeId?: string | null;
  oggetto?: string | null;
}): string {
  const params = new URLSearchParams();
  if (prefill?.clienteId) params.set('clienteId', prefill.clienteId);
  if (prefill?.macchinaId) params.set('macchinaId', prefill.macchinaId);
  if (prefill?.sedeId) params.set('sedeId', prefill.sedeId);
  if (prefill?.oggetto) params.set('oggetto', prefill.oggetto);
  const query = params.toString();
  return query ? `${paths.ticket.nuovo}?${query}` : paths.ticket.nuovo;
}

export function machineAlertsHref(filters?: {
  stato?: string | string[];
  servizio?: string | string[];
  clienteId?: string | null;
  sedeId?: string | null;
  macchinaId?: string | null;
  stale?: boolean;
  recent?: boolean;
  isAppliance?: boolean;
  view?: 'heatmap' | 'table';
  drawer?: string | null;
}): string {
  const params = new URLSearchParams();
  const append = (key: string, value: string | string[] | boolean | null | undefined): void => {
    if (value === undefined || value === null || value === '') return;
    if (typeof value === 'boolean') {
      if (value) params.set(key, 'true');
      return;
    }
    const values = Array.isArray(value) ? value : [value];
    values.forEach((entry) => {
      if (entry) params.append(key, entry);
    });
  };
  append('stato', filters?.stato);
  append('servizio', filters?.servizio);
  append('clienteId', filters?.clienteId);
  append('sedeId', filters?.sedeId);
  append('macchinaId', filters?.macchinaId);
  append('stale', filters?.stale);
  append('recent', filters?.recent);
  append('isAppliance', filters?.isAppliance);
  append('view', filters?.view);
  append('drawer', filters?.drawer);
  const query = params.toString();
  return query ? `${paths.admin.backup.alert}?${query}` : paths.admin.backup.alert;
}

/** Landing after login: company operators on in-progress tickets, billing staff on cedolini, technical staff on the operational dashboard, tenant admins on their panoramica. */
export function homePath(ruolo: Ruolo | null | undefined): string {
  if (ruolo === 'cliente_user') return paths.ticket.inLavorazione;
  if (ruolo === 'operatore_amministrativo') return paths.admin.cedolini;
  if (isStaffRole(ruolo)) return paths.admin.operativa;
  return paths.dashboard;
}
